import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, InputNumber, Tag, Alert, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/functions";
import { getCountFromServer, collection, query, where } from "firebase/firestore";

import moment from "moment";

const AR_TYPES = [
  { id: "recommande_ar", label: "Recommandé avec AR", defaultPrefix: "2C" },
  { id: "recommande_sans_ar", label: "Recommandé sans AR", defaultPrefix: "" },
  { id: "ar_international", label: "AR International", defaultPrefix: "" },
  { id: "lettre_suivie", label: "Lettre suivie", defaultPrefix: "" },
];

const STATUS_COLORS = {
  active: "green",
  depleted: "red",
  archived: "default",
};

const STATUS_LABELS = {
  active: "Active",
  depleted: "Épuisée",
  archived: "Archivée",
};

export default function ARPoolsScreen() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);

  const [form] = Form.useForm();

  const db = firebase.firestore();
  const [importARPool] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("importARPool", { timeout: 540000 })
  );

  async function fetchPools() {
    setLoading(true);
    try {
      const snapshot = await db.collection("tracking_pools").orderBy("created_at", "desc").get();
      const data = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const pool = { id: doc.id, ...doc.data() };
          const q = query(
            collection(db.app.firestore(), "tracking_numbers"),
            where("pool_id", "==", doc.id),
            where("status", "==", "AVAILABLE")
          );
          const countSnap = await getCountFromServer(q);
          pool.available_count = countSnap.data().count;
          return pool;
        })
      );
      setPools(data);
    } catch (error) {
      console.error("Error fetching AR pools:", error);
      message.error("Erreur lors du chargement des plages");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPools();
  }, []);

  const [calculatedCount, setCalculatedCount] = useState(null);

  function handleValuesChange(_, allValues) {
    const { startNumber, endNumber } = allValues;
    if (startNumber && endNumber && endNumber >= startNumber) {
      setCalculatedCount(endNumber - startNumber + 1);
    } else {
      setCalculatedCount(null);
    }
  }

  // Handle type change → auto-fill prefix
  function handleTypeChange(value) {
    const arType = AR_TYPES.find((t) => t.id === value);
    if (arType && arType.defaultPrefix) {
      form.setFieldsValue({ prefix: arType.defaultPrefix });
    }
  }

  // Submit
  async function handleSubmit() {
    try {
      const values = await form.validateFields();
      setImporting(true);

      const result = await importARPool({
        name: values.name,
        type: values.type,
        prefix: values.prefix,
        startNumber: values.startNumber,
        endNumber: values.endNumber,
        notes: values.notes || "",
      });

      message.success(`Plage créée — ${result.data.totalCreated} numéros générés`);
      setModalVisible(false);
      form.resetFields();
      fetchPools();
    } catch (error) {
      console.error("Error importing AR pool:", error);
      const errorMsg = error?.details?.message || error?.message || "Erreur lors de la création de la plage";
      message.error(errorMsg);
    } finally {
      setImporting(false);
    }
  }

  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => AR_TYPES.find((t) => t.id === type)?.label || type,
    },
    {
      title: "Préfixe",
      dataIndex: "prefix",
      key: "prefix",
    },
    {
      title: "Plage",
      key: "range",
      render: (_, record) => `${record.start_number} → ${record.end_number}`,
    },
    {
      title: "Disponibles / Total",
      key: "available",
      render: (_, record) => {
        const available = record.available_count ?? "—";
        const total = record.total_count?.toLocaleString("fr-FR");
        const isLow = record.available_count != null && record.available_count <= 5000;
        return (
          <span style={isLow ? { color: "#ff4d4f", fontWeight: "bold" } : undefined}>
            {typeof available === "number" ? available.toLocaleString("fr-FR") : available} / {total}
          </span>
        );
      },
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status] || status}</Tag>
      ),
    },
    {
      title: "Créée le",
      dataIndex: "created_at",
      key: "created_at",
      render: (ts) => (ts ? moment(ts.toDate()).format("DD/MM/YYYY HH:mm") : "—"),
    },
  ];

  // Aggregate available counts by type for alerts
  const alertsByType = AR_TYPES.reduce((acc, arType) => {
    const poolsForType = pools.filter((p) => p.type === arType.id && p.status !== "archived");
    if (poolsForType.length === 0) return acc;
    const total = poolsForType.reduce((sum, p) => sum + (p.available_count || 0), 0);
    if (total < 5000) {
      acc.push({ ...arType, remaining: total });
    }
    return acc;
  }, []);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Plages de numéros AR</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
          Nouvelle plage
        </Button>
      </div>

      {alertsByType.map((alert) => (
        <Alert
          key={alert.id}
          type={alert.remaining <= 2500 ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            alert.remaining <= 2500
              ? `Stock critique : ${alert.remaining.toLocaleString("fr-FR")} numéros "${alert.label}" restants`
              : `Stock bas : ${alert.remaining.toLocaleString("fr-FR")} numéros "${alert.label}" restants`
          }
        />
      ))}

      <Table
        dataSource={pools}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: "Aucune plage de numéros" }}
      />

      <Modal
        title="Ajouter une plage de numéros"
        visible={modalVisible}
        onCancel={() => {
          if (!importing) {
            setModalVisible(false);
            form.resetFields();
          }
        }}
        onOk={handleSubmit}
        okText={importing ? "Création en cours..." : "Créer la plage"}
        cancelText="Annuler"
        confirmLoading={importing}
        closable={!importing}
        maskClosable={!importing}
      >
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
          <Form.Item
            name="name"
            label="Nom de la plage"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Input placeholder="Ex: Pool 2026-Q1 - Recommandé avec AR" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Select placeholder="Sélectionner le type" onChange={handleTypeChange}>
              {AR_TYPES.map((t) => (
                <Select.Option key={t.id} value={t.id}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="prefix"
            label="Préfixe"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Input placeholder="Ex: 2C" />
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item
              name="startNumber"
              label="Numéro début"
              rules={[{ required: true, message: "Requis" }]}
              style={{ flex: 1 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="1976899972"
                controls={false}
              />
            </Form.Item>

            <Form.Item
              name="endNumber"
              label="Numéro fin"
              rules={[{ required: true, message: "Requis" }]}
              style={{ flex: 1 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="1976924971"
                controls={false}
              />
            </Form.Item>
          </div>

          {calculatedCount && (
            <p style={{ color: "#1890ff", marginTop: -8 }}>
              {calculatedCount.toLocaleString("fr-FR")} numéros seront créés.
            </p>
          )}

          <Form.Item name="notes" label="Notes">
            <Input.TextArea placeholder="Ex: N° compte suivi 404109 — Renouvellement" rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
